import { api } from "@/lib/api";

/**
 * Types for payment settings and methods
 */
export type PaymentSettings = {
  onlinePayments: boolean;
  paypalPayments: boolean;
  payLater: boolean;
  bankTransfer: boolean;
  twint: boolean;
  twintPhoneNumber?: string | null;
  twintQrCode?: string | null;
  bankDetails?:
    | string
    | {
        accountName: string;
        accountNumber: string;
      }
    | null;
  stripeConnected?: boolean;
  stripeAccountId?: string | null;
  isTwintEnabledInStripe?: boolean;
  paypalAccountId?: string | null;
};

export type PaymentMethod =
  | "CARD"
  | "CASH"
  | "TRANSFER"
  | "PAYPAL"
  | "TWINT"
  | "ADDITIONAL";

/**
 * Fetches payment settings from the API
 * @returns Promise containing payment settings
 */

export const fetchPaymentSettings = async (): Promise<PaymentSettings> => {
  try {
    const data = await api("/api/widget/payment-settings");

    if (!data?.settings) {
      throw new Error("Failed to fetch payment settings");
    }

    return data.settings;
  } catch (error) {
    console.error("Error fetching payment settings:", error);
    // Return default settings if fetch fails
    return {
      onlinePayments: false,
      paypalPayments: false,
      payLater: true, // Default to cash payments
      bankTransfer: false,
      twint: false,
      twintPhoneNumber: null,
      bankDetails: null,
    };
  }
};

/**
 * Validates whether a payment method is enabled based on settings
 * @param method Payment method to check
 * @param settings Payment settings
 * @returns Boolean indicating if the method is enabled
 */
export const isPaymentMethodEnabled = (
  method: PaymentMethod,
  settings: PaymentSettings
): boolean => {
  switch (method) {
    case "CARD":
      return settings.onlinePayments;
    case "CASH":
      return settings.payLater;
    case "TRANSFER":
      return settings.bankTransfer;
    case "PAYPAL":
      return settings.paypalPayments;
    case "TWINT":
      return settings.twint;
    default:
      return false;
  }
};

/**
 * Gets available payment methods based on settings
 * @param settings Payment settings
 * @returns Array of enabled payment methods
 */
export const getAvailablePaymentMethods = (
  settings: PaymentSettings
): PaymentMethod[] => {
  const methods: PaymentMethod[] = [];

  if (settings.payLater) methods.push("CASH");
  if (settings.bankTransfer) methods.push("TRANSFER");
  if (settings.onlinePayments) methods.push("CARD");
  if (settings.paypalPayments) methods.push("PAYPAL");
  if (settings.twint) methods.push("TWINT");

  return methods;
};

/**
 * Parses bank details from settings
 * @param settings Payment settings
 * @returns Parsed bank details or null
 */
export const parseBankDetails = (settings: PaymentSettings) => {
  if (!settings.bankDetails) return null;

  try {
    if (typeof settings.bankDetails === "string") {
      return JSON.parse(settings.bankDetails);
    }
    return settings.bankDetails;
  } catch (e) {
    console.error("Error parsing bank details:", e);
    return null;
  }
};

/**
 * Gets TWINT phone number from settings
 * @param settings Payment settings
 * @returns TWINT phone number or null
 */
export const getTwintPhoneNumber = (
  settings: PaymentSettings
): string | null => {
  return settings.twintPhoneNumber || null;
};

/**
 * Gets TWINT QR code file key from settings
 * @param settings Payment settings
 * @returns TWINT QR code file key or null
 */
export const getTwintQrCode = (settings: PaymentSettings): string | null => {
  return settings.twintQrCode || null;
};
